import { useEffect, useState } from 'react';
import { Download, Printer, X } from 'lucide-react';
import type { AttendanceCertificateFormData } from '../../services/AttendanceCertificatePDFService';

interface AttendanceCertificateModalProps {
  initialData: AttendanceCertificateFormData;
  isSubmitting: boolean;
  onClose: () => void;
  onDownload: (data: AttendanceCertificateFormData) => Promise<void>;
  onPrint: (data: AttendanceCertificateFormData) => Promise<void>;
}

const buildSignatureLabel = (data: AttendanceCertificateFormData): string => {
  if (data.signatureLabel.trim()) return data.signatureLabel.trim();
  if (data.professionalName.trim()) return data.professionalName.trim();
  return 'Profissional responsável';
};

export default function AttendanceCertificateModal({
  initialData,
  isSubmitting,
  onClose,
  onDownload,
  onPrint,
}: AttendanceCertificateModalProps) {
  const [formData, setFormData] = useState<AttendanceCertificateFormData>(initialData);

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const updateField = <K extends keyof AttendanceCertificateFormData>(
    key: K,
    value: AttendanceCertificateFormData[K],
  ) => {
    setFormData((current) => {
      const next = {
        ...current,
        [key]: value,
        signature: null,
      };
      if (key === 'professionalName' && !current.signatureLabel.trim()) {
        next.signatureLabel = String(value || '');
      }
      return next;
    });
  };

  const normalizedData: AttendanceCertificateFormData = {
    ...formData,
    signatureLabel: buildSignatureLabel(formData),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Atestado de presença</h2>
            <p className="text-sm text-slate-500">Revise e edite os dados antes de baixar ou imprimir.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-6 px-6 py-6 lg:grid-cols-2">
          <section className="space-y-4">
            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Clínica e emissão</h3>
              <div className="mt-4 grid gap-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                  <p className="font-medium text-slate-800">{formData.organizationName}</p>
                  <p className="mt-1">
                    Cabeçalho e rodapé seguem automaticamente a identidade visual do tenant ativo.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-slate-700">Data de emissão</span>
                    <input
                      type="date"
                      value={formData.issueDate}
                      onChange={(event) => updateField('issueDate', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-slate-700">Cidade/UF de emissão</span>
                    <input
                      type="text"
                      value={formData.issueLocation}
                      onChange={(event) => updateField('issueLocation', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Paciente</h3>
              <div className="mt-4 grid gap-4">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-slate-700">Nome completo</span>
                  <input
                    type="text"
                    value={formData.patientName}
                    onChange={(event) => updateField('patientName', event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-slate-700">CPF</span>
                    <input
                      type="text"
                      value={formData.patientCpf}
                      onChange={(event) => updateField('patientCpf', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-slate-700">RG</span>
                    <input
                      type="text"
                      value={formData.patientRg}
                      onChange={(event) => updateField('patientRg', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                </div>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-slate-700">Endereço</span>
                  <textarea
                    value={formData.patientAddress}
                    onChange={(event) => updateField('patientAddress', event.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Sessão</h3>
              <div className="mt-4 grid gap-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <label className="block text-sm sm:col-span-1">
                    <span className="mb-1 block font-medium text-slate-700">Data</span>
                    <input
                      type="date"
                      value={formData.appointmentDate}
                      onChange={(event) => updateField('appointmentDate', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-slate-700">Hora inicial</span>
                    <input
                      type="time"
                      value={formData.appointmentStartTime}
                      onChange={(event) => updateField('appointmentStartTime', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-slate-700">Hora final</span>
                    <input
                      type="time"
                      value={formData.appointmentEndTime}
                      onChange={(event) => updateField('appointmentEndTime', event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </label>
                </div>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-slate-700">Tipo de atendimento</span>
                  <input
                    type="text"
                    value={formData.attendanceType}
                    onChange={(event) => updateField('attendanceType', event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-slate-700">Local do atendimento</span>
                  <input
                    type="text"
                    value={formData.attendanceLocation}
                    onChange={(event) => updateField('attendanceLocation', event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Profissional responsável</h3>
              <div className="mt-4 grid gap-4">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-slate-700">Nome</span>
                  <input
                    type="text"
                    value={formData.professionalName}
                    onChange={(event) => updateField('professionalName', event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-slate-700">Registro profissional</span>
                  <input
                    type="text"
                    value={formData.professionalRegistration}
                    onChange={(event) => updateField('professionalRegistration', event.target.value)}
                    placeholder="Ex: CREFITO-8/123456"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-slate-700">Linha de assinatura</span>
                  <input
                    type="text"
                    value={formData.signatureLabel}
                    onChange={(event) => updateField('signatureLabel', event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Observações opcionais</h3>
              <label className="mt-4 block text-sm">
                <textarea
                  value={formData.additionalNotes}
                  onChange={(event) => updateField('additionalNotes', event.target.value)}
                  rows={4}
                  placeholder="Campo opcional para observações adicionais."
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </label>
            </div>
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={() => void onPrint(normalizedData)}
            disabled={isSubmitting}
            className="inline-flex items-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Printer className="mr-2 h-4 w-4" />
            Imprimir
          </button>
          <button
            type="button"
            onClick={() => void onDownload(normalizedData)}
            disabled={isSubmitting}
            className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Download className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Gerando...' : 'Baixar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
